/**
 * Sesión del usuario.
 *
 * La verdad de quién eres y de qué puedes hacer la tiene el backend: la capa de
 * alcance es la única puerta a los datos, y responde 404 al inquilino ajeno sin
 * preguntarle al frontend. Este contexto solo cachea el perfil para que la
 * interfaz pueda mostrar u ocultar cosas: esconder un botón no es un control de
 * acceso, es una cortesía.
 *
 * Un único `useYo()` alimenta todo. Al vivir en la caché de TanStack Query, los
 * veinte componentes que preguntan por el rol comparten una sola petición.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Yo } from '@compranorte/contracts'
import { esSuspension } from '../api/cliente'
import { useYo } from '../api/hooks'

export interface Sesion {
  /** null mientras carga, y también cuando no hay sesión. */
  yo: Yo | null
  cargando: boolean
  /**
   * Sesión existente cuya cuenta está desactivada. Se distingue de `yo: null` a
   * propósito, porque no es lo mismo no haber entrado que haber sido
   * suspendido: al segundo hay que decírselo, no dejarlo mirando una pantalla
   * vacía. Es el acierto de la v1 que se conserva.
   */
  suspendida: boolean
  /** Equipo interno de Compranorte: admin u operador. */
  esEquipo: boolean
  esAdmin: boolean
  /** Un cliente compra; el equipo también, para emitir a nombre de un cliente. */
  puedeComprar: boolean
  /**
   * Puede ESCRIBIR a nombre de su empresa: comprar, publicar, mover stock.
   *
   * El equipo pasa siempre y un `lector` nunca. Es una CORTESÍA de la interfaz
   * —esconder un botón que va a responder 403 es mejor que dejarlo puesto— y no
   * un control: cada endpoint comprueba el nivel por su cuenta.
   */
  esOperativo: boolean
  /** Administra las cuentas de su propia empresa. Le habilita la pestaña. */
  administraEmpresa: boolean
}

const Ctx = createContext<Sesion | null>(null)

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const consulta = useYo()

  const valor = useMemo<Sesion>(() => {
    const yo = consulta.data ?? null
    const rol = yo?.rol
    const esEquipo = rol === 'admin' || rol === 'operador'
    return {
      yo,
      /*
       * `isPending` a secas NO sirve: cuando la consulta está deshabilitada
       * —lo que ocurre con todo visitante anónimo, porque no preguntamos por el
       * perfil si no hay marca de sesión— TanStack Query la deja en `pending`
       * para siempre. La aplicación mostraría "Cargando…" eternamente.
       *
       * `fetchStatus === 'idle'` distingue "esperando respuesta" de "nunca va a
       * pedir nada", que es lo que de verdad hace falta saber.
       */
      cargando: consulta.isPending && consulta.fetchStatus !== 'idle',
      suspendida: esSuspension(consulta.error),
      esEquipo,
      esAdmin: rol === 'admin',
      puedeComprar: rol === 'cliente' || esEquipo,
      /*
       * Sin nivel se cae a `operativo`, igual que en el servidor y por el mismo
       * motivo: es el permiso que la cuenta tenía antes de que hubiera niveles.
       * Caer a `lector` habría escondido el botón de comprar a media plataforma
       * mientras la migración terminaba de correr.
       */
      esOperativo: esEquipo || (yo?.nivelEmpresa ?? 'operativo') !== 'lector',
      administraEmpresa: yo?.nivelEmpresa === 'administrador',
    }
  }, [consulta.data, consulta.isPending, consulta.fetchStatus, consulta.error])

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>
}

export function useSesion(): Sesion {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useSesion debe usarse dentro de <ProveedorSesion>')
  return ctx
}
